import React, { useEffect } from 'react';
import { useAppSelector } from 'redux/hooks';
import { adobeAnalyticsScriptsSelector } from '@doda/common/src/redux/features/analytics';
import { JobDetailProps } from '.';
import { EmploymentStatus } from './EmploymentStatus';
import { WorkingTime } from './WorkingTime';
import { Location } from './Location';
import { RequirementHead } from './RequirementHead';
import { createJobProperty } from './logics/createJobProperty';
import styles from './index.module.scss';
import { JobDescription } from './JobDescription';
import { TargetMember } from './TargetMember';
import { FreeTextArea } from './FreeTextArea';
import { ApplicationMethod } from './ApplicationMethod';
import { Salary } from './Salary';
import { TreatmentAndWelfare } from './TreatmentAndWelfare';
import { Holiday } from './Holiday';
import { PromoteApplicationModalDisplayTarget } from '../../PromoteApplicationModal';
import { EmployeeInterview } from '../EmployeeInterview';
import { InterviewReport } from '../InterviewReport';
import { CompanyPageLinkBanner } from './CompanyPageLinkBanner';
import { CompanyOverview } from './CompanyOverview';
import { ThreeImages } from './ThreeImages';
import { measureVisibleCompanyPageLinkBanner } from '../../../logics/analytics';
import { JobOfferKindAgent } from './JobOfferKindAgent';
import { JobGlossaryLink } from '../JobGlossaryLink';
import { SectionTitle } from '../SectionTitle';
import { SelectionPoint } from './SelectionPoint';
import { ReasonOfLimitAge } from './ReasonOfLimitAge';

/**
 * C原稿のProps
 */
interface ManuscriptCProps extends JobDetailProps {}

/**
 * C原稿
 */
export const ManuscriptC: React.FC<ManuscriptCProps> = ({ job, isScout }) => {
  const isAdobeScript = useAppSelector(adobeAnalyticsScriptsSelector);
  const {
    jobState,
    workLocations,
    workTime,
    averageOvertimeHours,
    averageOvertimeHoursExplanation,
    formDivision,
    period,
    jobContentOutline,
    jobContentDetail,
    teamStructure,
    organizationCategory,
    genderRatio,
    ageComposition,
    retentionRate,
    projectCase,
    projectCaseTitle,
    developmentEnvironment,
    developmentEnvironmentTitle,
    targetMemberOutline,
    targetMemberDetail,
    freeSpaces,
    jobOfferKind,
    selectionInformation,
    companyInfo,
    corporateFullName,
    occupationName,
    recruit,
    treatmentAndWelfare,
    companyBenefits,
    interview,
    employeeInterviews,
    hasCompanyPageLink,
    companyId,
    corporateName,
    businessOutline,
    marketName,
    picturePublicImages,
    jid,
    ageLimitDisplay,
    selectionPoint,
    isAgent,
    isRead,
    hasArticleContent,
  } = createJobProperty(job);
  const isVisibleCompanyPageLinkBanner = Boolean(
    hasCompanyPageLink && corporateName && companyId && jid
  );
  useEffect(() => {
    if (isAdobeScript) {
      measureVisibleCompanyPageLinkBanner(isVisibleCompanyPageLinkBanner, jid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdobeScript]);
  return (
    <div>
      {companyInfo?.companyOverviewHeading && (
        <div className={styles['jobSearchDetail-body__requirementHead']}>
          <RequirementHead
            companyInfo={companyInfo}
            corporateFullName={corporateFullName}
            occupationName={occupationName}
          />
        </div>
      )}
      <SectionTitle mb="L" border sideItem={<JobGlossaryLink />}>
        募集要項
      </SectionTitle>
      <JobDescription
        jobContentOutline={jobContentOutline}
        jobContentDetail={jobContentDetail}
        teamStructure={teamStructure}
        organizationCategory={organizationCategory}
        genderRatio={genderRatio}
        ageComposition={ageComposition}
        retentionRate={retentionRate}
        projectCase={projectCase}
        projectCaseTitle={projectCaseTitle}
        developmentEnvironment={developmentEnvironment}
        developmentEnvironmentTitle={developmentEnvironmentTitle}
      />
      <div className={styles['jobSearchDetail-body__sectionItem']}>
        <TargetMember
          targetMemberOutline={targetMemberOutline}
          targetMemberDetail={targetMemberDetail}
        />
      </div>
      {ageLimitDisplay && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <ReasonOfLimitAge ageLimitDisplay={ageLimitDisplay} />
        </div>
      )}
      {selectionPoint && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <SelectionPoint selectionPoint={selectionPoint} />
        </div>
      )}
      <div className={styles['jobSearchDetail-body__sectionItem']}>
        <Location jobState={jobState} workLocations={workLocations} />
      </div>
      {(workTime || averageOvertimeHours) && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <WorkingTime
            workTime={workTime}
            averageOverworkHours={averageOvertimeHours}
            averageOverworkHoursExplanation={averageOvertimeHoursExplanation}
          />
        </div>
      )}
      <div className={styles['jobSearchDetail-body__sectionItem']}>
        <EmploymentStatus formDivision={formDivision} period={period} />
      </div>
      {recruit && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <PromoteApplicationModalDisplayTarget {...{ isScout, isRead }}>
            <Salary recruit={recruit} />
          </PromoteApplicationModalDisplayTarget>
        </div>
      )}
      {(treatmentAndWelfare || companyBenefits) && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <TreatmentAndWelfare
            treatmentAndWelfare={treatmentAndWelfare}
            companyBenefits={companyBenefits}
          />
        </div>
      )}
      {recruit && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <Holiday recruit={recruit} />
        </div>
      )}
      {freeSpaces?.length > 0 && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <FreeTextArea freeSpaces={freeSpaces} />
        </div>
      )}
      {employeeInterviews.length !== 0 && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <EmployeeInterview
            employeeInterviews={interview?.employeeInterviews || []}
          />
        </div>
      )}
      {interview?.reportKind && interview?.reportTitle && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <InterviewReport interview={interview} />
        </div>
      )}
      {picturePublicImages.length !== 0 && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <ThreeImages picturePublicImages={picturePublicImages} />
        </div>
      )}
      {!isAgent && selectionInformation && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <ApplicationMethod
            selectionInformation={selectionInformation}
            jobOfferKind={jobOfferKind}
          />
        </div>
      )}
      {businessOutline && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <CompanyOverview
            businessOutline={businessOutline}
            marketName={marketName}
          />
        </div>
      )}
      {isVisibleCompanyPageLinkBanner && (
        <div className={styles['jobSearchDetail-body__companyPageLink']}>
          <CompanyPageLinkBanner
            companyId={companyId}
            corporateName={corporateName}
            jid={jid}
            hasArticleContent={hasArticleContent}
          />
        </div>
      )}
      {isAgent && (
        <div className={styles['jobSearchDetail-body__sectionItem']}>
          <JobOfferKindAgent />
        </div>
      )}
    </div>
  );
};
